import { CoreEnums } from '@/utils/domain/constants';
import { resolveSocketType } from '@/utils/infrastructure/notifications/describeNotification';
import {
  deleteAllNotifications as deleteAllNotificationsRequest,
  deleteNotification as deleteNotificationRequest,
  fetchNotificationCount,
  fetchNotificationsPaged,
  markAllNotificationsAsRead as markAllNotificationsAsReadRequest,
  markNotificationAsRead as markNotificationAsReadRequest,
} from '@/services/notifications';
import { generatePublicFileURL } from '@/utils/infrastructure';
import _ from 'lodash';
import moment from 'moment';
import { formatMessage } from 'umi';

const DEFAULT_PAGE_SIZE = 20;

const SOCKET_MESSAGE_TYPE = CoreEnums.SocketMessageType || {};

const API_NOTIFICATION_TYPE_MAP = {
  [SOCKET_MESSAGE_TYPE.All]: CoreEnums.NotificationType.System,
  [SOCKET_MESSAGE_TYPE.ProcessInstance]: CoreEnums.NotificationType.Process,
  [SOCKET_MESSAGE_TYPE.List]: CoreEnums.NotificationType.List,
  [SOCKET_MESSAGE_TYPE.Entity]: CoreEnums.NotificationType.Entity,
  [SOCKET_MESSAGE_TYPE.Chat]: CoreEnums.NotificationType.Chat,
  [SOCKET_MESSAGE_TYPE.BulkRequest]: 'bulk-request',
  [SOCKET_MESSAGE_TYPE.ConfigurationEntity]: 'configuration-entity',
  [SOCKET_MESSAGE_TYPE.Report]: CoreEnums.NotificationType.Report,
  [SOCKET_MESSAGE_TYPE.DocumentExport]: CoreEnums.NotificationType.Document,
};

const shouldAddSocketNotification = (notification) => {
  const socketType = resolveSocketType(notification?.type);
  const outerType =
    typeof notification?.data?.type === 'string' ? notification.data.type.toLowerCase() : '';
  const nestedType =
    typeof notification?.data?.data?.type === 'string'
      ? notification.data.data.type.toLowerCase()
      : '';
  const dataType =
    (outerType === 'process-instance' || outerType === 'chat') && nestedType
      ? nestedType
      : outerType || null;

  switch (socketType) {
    case SOCKET_MESSAGE_TYPE.ProcessInstance:
      return ['new-task-assigned', 'new-comment'].includes(dataType);
    case SOCKET_MESSAGE_TYPE.Entity:
    case SOCKET_MESSAGE_TYPE.List:
    case SOCKET_MESSAGE_TYPE.Chat:
    case SOCKET_MESSAGE_TYPE.BulkRequest:
    case SOCKET_MESSAGE_TYPE.ConfigurationEntity:
      return true;
    case SOCKET_MESSAGE_TYPE.Report:
    case SOCKET_MESSAGE_TYPE.DocumentExport: {
      const taskStatus = notification?.data?.taskStatus;
      const finalStatuses = [
        CoreEnums.InternalTaskStatus.Processed,
        CoreEnums.InternalTaskStatus.Error,
        CoreEnums.InternalTaskStatus.Failed,
        CoreEnums.InternalTaskStatus.Cancelled,
        'processed',
        'error',
        'cancelled',
      ];
      return finalStatuses.includes(taskStatus);
    }
    default:
      return false;
  }
};

const isLocalNotification = (notificationId) => {
  return typeof notificationId === 'string' && notificationId.startsWith('notification_');
};

const calculateUnreadCount = (notifications = []) => {
  return notifications.filter((notification) => notification && !notification.isRead).length;
};

const stripHtml = (value) => {
  if (!value || typeof value !== 'string') return '';
  return value
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const normalizeNotificationFromApi = (rawNotification) => {
  if (!rawNotification) return null;

  const normalizedType =
    typeof rawNotification.type === 'string'
      ? rawNotification.type
      : API_NOTIFICATION_TYPE_MAP[rawNotification.type] || CoreEnums.NotificationType.System;

  const timestamp = rawNotification.created || rawNotification.updated || rawNotification.timestamp;
  const eventType = rawNotification?.data?.type || rawNotification?.eventType || null;
  const contextPayload =
    rawNotification?.data?.data && typeof rawNotification.data.data === 'object'
      ? rawNotification.data.data
      : null;

  const baseTitle =
    rawNotification.title ||
    rawNotification?.data?.title ||
    formatMessage({ id: 'notifications.generic.title' });
  const baseMessage =
    rawNotification.message ||
    rawNotification?.data?.message ||
    rawNotification?.data?.data?.processInstanceName ||
    formatMessage({ id: 'notifications.generic.message' });

  let resolvedTitle = baseTitle;
  let resolvedMessage = baseMessage;

  if (normalizedType === CoreEnums.NotificationType.Process) {
    switch (eventType) {
      case 'new-task-assigned':
        resolvedTitle =
          contextPayload?.processInstanceName ||
          baseTitle ||
          formatMessage({ id: 'notifications.processEvents.newTaskAssigned.title' });
        resolvedMessage = formatMessage({ id: 'notifications.processEvents.newTaskAssigned.body' });
        break;
      case 'new-comment': {
        const commentText = stripHtml(contextPayload?.commentData?.commentText);
        resolvedTitle =
          contextPayload?.processInstanceName ||
          baseTitle ||
          formatMessage({ id: 'notifications.processEvents.newComment.title' });
        resolvedMessage =
          commentText || formatMessage({ id: 'notifications.processEvents.newComment.body' });
        break;
      }
      default:
        resolvedTitle = contextPayload?.processInstanceName || baseTitle;
        break;
    }
  }

  return {
    id: rawNotification.id || _.uniqueId('notification_'),
    type: normalizedType,
    title: resolvedTitle,
    message: resolvedMessage,
    data: rawNotification.data || {},
    timestamp: timestamp ? moment.utc(timestamp).toISOString() : moment.utc().toISOString(),
    isRead:
      typeof rawNotification.isRead === 'boolean'
        ? rawNotification.isRead
        : Boolean(rawNotification.read),
    priority: rawNotification.priority || CoreEnums.NotificationPriority.Normal,
    actions: rawNotification.actions || [],
  };
};

const mergeNotificationLists = (current = [], incoming = [], { append = false } = {}) => {
  const sanitizedCurrent = current.filter(Boolean);
  const sanitizedIncoming = incoming.filter(Boolean);
  if (!append) {
    return _.uniqBy(sanitizedIncoming, 'id');
  }
  return _.uniqBy([...sanitizedCurrent, ...sanitizedIncoming], 'id');
};

// Helper functions for i18n
const getOperationTypeText = (operationType) => {
  if (
    operationType === CoreEnums.DynamicEntityInternalTaskOperationType.Export ||
    operationType === CoreEnums.CustomListInternalTaskOperationType.Export
  ) {
    return formatMessage({ id: 'notifications.operationType.export' });
  }
  if (operationType === CoreEnums.DynamicEntityInternalTaskOperationType.Import) {
    return formatMessage({ id: 'notifications.operationType.import' });
  }
  return formatMessage({ id: 'notifications.operationType.default' });
};

// TODO: comment for functions like this one, the solution for this kind of definitions can be simpler
const getStatusText = (taskStatus) => {
  switch (taskStatus) {
    case CoreEnums.InternalTaskStatus.Pending:
      return formatMessage({ id: 'notifications.statusText.pending' });
    case CoreEnums.InternalTaskStatus.Processing:
      return formatMessage({ id: 'notifications.statusText.processing' });
    case CoreEnums.InternalTaskStatus.Processed:
      return formatMessage({ id: 'notifications.statusText.processed' });
    case CoreEnums.InternalTaskStatus.Failed:
      return formatMessage({ id: 'notifications.statusText.failed' });
    case CoreEnums.InternalTaskStatus.Error:
      return formatMessage({ id: 'notifications.statusText.error' });
    case CoreEnums.InternalTaskStatus.Cancelled:
      return formatMessage({ id: 'notifications.statusText.cancelled' });
    case CoreEnums.InternalTaskStatus.Skipped:
      return formatMessage({ id: 'notifications.statusText.skipped' });
    default:
      return formatMessage({ id: 'notifications.statusText.unknown' });
  }
};

const initialState = {
  // Drawer state
  drawerVisible: false,

  // Notifications list
  notifications: [],
  initialized: false,
  hasMore: true,

  // Unread notifications counter
  unreadCount: 0,

  // Loading state
  loading: false,
  isFetchingMore: false,
  error: null,

  // Pagination for future implementations
  pagination: {
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
    count: 0,
    totalPages: 0,
  },

  // Filters for future implementations
  filters: {
    type: 'all', // 'all', 'entity', 'process', etc.
    status: 'all', // 'all', 'read', 'unread'
  },
};

const NotificationsModel = {
  namespace: 'notifications',
  state: initialState,

  effects: {
    // Effect to open/close the drawer
    *toggleDrawer({ payload }, { put, select }) {
      try {
        const currentState = yield select((state) => state.notifications.drawerVisible);
        yield put({
          type: 'updateDrawerVisible',
          payload: payload !== undefined ? payload : !currentState,
        });
      } catch (error) {
        yield put({
          type: 'updateDrawerVisible',
          payload: false,
        });
      }
    },

    *fetchNotifications({ payload = {} }, { call, put, select }) {
      const { append = false, reset = false, limit, orderBy, filter } = payload;
      const state = yield select((store) => store.notifications);
      const nextLimit = limit || state.pagination.limit || DEFAULT_PAGE_SIZE;
      const nextPage = reset ? 1 : append ? (state.pagination.page || 1) + 1 : payload.page || 1;

      if (append && (!state.hasMore || state.isFetchingMore)) {
        return;
      }

      if (!append) {
        yield put({
          type: 'updateLoading',
          payload: true,
        });
      } else {
        yield put({
          type: 'setFetchingMore',
          payload: true,
        });
      }

      try {
        const response = yield call(fetchNotificationsPaged, {
          page: nextPage,
          limit: nextLimit,
          orderBy,
          filter,
        });

        const normalizedItems = (response?.items || [])
          .map(normalizeNotificationFromApi)
          .filter(Boolean);

        const notifications = mergeNotificationLists(state.notifications, normalizedItems, {
          append: append && !reset,
        });

        const totalCount = Number(response?.totalCount ?? 0);
        const hasTotal = !Number.isNaN(totalCount) && totalCount >= 0;
        const derivedHasMore = hasTotal
          ? nextPage * nextLimit < totalCount
          : normalizedItems.length === nextLimit;
        const derivedTotalPages = hasTotal
          ? nextLimit
            ? Math.ceil(totalCount / nextLimit)
            : 0
          : nextLimit
          ? Math.ceil(notifications.length / nextLimit)
          : state.pagination.totalPages;

        yield put({
          type: 'updateNotifications',
          payload: {
            notifications,
            pagination: {
              page: nextPage,
              limit: nextLimit,
              count: hasTotal ? totalCount : notifications.length,
              totalPages: derivedTotalPages,
            },
            hasMore: derivedHasMore,
            initialized: true,
          },
        });
        yield put({
          type: 'setError',
          payload: null,
        });
      } catch (error) {
        yield put({
          type: 'setError',
          payload: error?.message || null,
        });
      } finally {
        if (!append) {
          yield put({
            type: 'updateLoading',
            payload: false,
          });
        } else {
          yield put({
            type: 'setFetchingMore',
            payload: false,
          });
        }
      }
    },

    *fetchUnreadCount(action, { call, put }) {
      try {
        const response = yield call(fetchNotificationCount);
        const parsed =
          typeof response === 'number'
            ? response
            : Number(
                typeof response === 'object' && response !== null && 'count' in response
                  ? response.count
                  : response,
              );
        const safeCount = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
        yield put({
          type: 'setUnreadCount',
          payload: safeCount,
        });
      } catch (error) {}
    },

    // Effect to add new notification
    *addNotification({ payload }, { put, select }) {
      try {
        const currentNotifications = yield select((state) => state.notifications.notifications);
        const currentUnreadCount = yield select((state) => state.notifications.unreadCount);

        // Create standard notification object
        const notification = {
          id: payload.id || _.uniqueId('notification_'),
          type: payload.type || CoreEnums.NotificationType.System,
          title: payload.title || formatMessage({ id: 'notifications.generic.title' }),
          message: payload.message || formatMessage({ id: 'notifications.generic.message' }),
          data: payload.data || {},
          timestamp: payload.timestamp
            ? moment.utc(payload.timestamp).toISOString()
            : moment.utc().toISOString(),
          isRead: payload.isRead ?? false,
          priority: payload.priority || CoreEnums.NotificationPriority.Normal,
          actions: payload.actions || [], // Array of available actions
        };

        // Add to the beginning of the list (most recent first)
        const updatedNotifications = _.uniqBy([notification, ...currentNotifications], 'id');

        yield put({
          type: 'updateNotifications',
          payload: {
            notifications: updatedNotifications,
            unreadCount: notification.isRead ? currentUnreadCount : currentUnreadCount + 1,
          },
        });
      } catch (error) {}
    },

    // Effect to mark notification as read
    *markAsRead({ payload }, { call, put, select }) {
      try {
        const { notificationId } = payload || {};
        if (!notificationId) return;

        if (!isLocalNotification(notificationId)) {
          try {
            yield call(markNotificationAsReadRequest, notificationId);
          } catch (error) {}
        }

        const notifications = yield select((state) => state.notifications.notifications);

        const updatedNotifications = notifications.map((notification) => {
          if (notification.id === notificationId && !notification.isRead) {
            return { ...notification, isRead: true };
          }
          return notification;
        });

        yield put({
          type: 'updateNotifications',
          payload: {
            notifications: updatedNotifications,
          },
        });
      } catch (error) {}
    },

    // Effect to mark all notifications as read
    *markAllAsRead(action, { call, put, select }) {
      try {
        try {
          yield call(markAllNotificationsAsReadRequest);
        } catch (error) {}

        const notifications = yield select((state) => state.notifications.notifications);

        const updatedNotifications = notifications.map((notification) => ({
          ...notification,
          isRead: true,
        }));

        yield put({
          type: 'updateNotifications',
          payload: {
            notifications: updatedNotifications,
          },
        });
      } catch (error) {}
    },

    // Effect to remove notification
    *removeNotification({ payload }, { call, put, select }) {
      try {
        const { notificationId } = payload || {};
        if (!notificationId) return;

        if (!isLocalNotification(notificationId)) {
          try {
            yield call(deleteNotificationRequest, notificationId);
          } catch (error) {}
        }

        const notifications = yield select((state) => state.notifications.notifications);

        const updatedNotifications = notifications.filter((n) => n.id !== notificationId);

        yield put({
          type: 'updateNotifications',
          payload: {
            notifications: updatedNotifications,
          },
        });
      } catch (error) {}
    },

    // Effect to clear all notifications
    *clearAllNotifications(action, { call, put }) {
      try {
        try {
          yield call(deleteAllNotificationsRequest);
        } catch (error) {}

        yield put({
          type: 'updateNotifications',
          payload: {
            notifications: [],
            pagination: {
              page: 1,
              limit: DEFAULT_PAGE_SIZE,
              count: 0,
              totalPages: 0,
            },
            hasMore: false,
            initialized: true,
          },
        });
      } catch (error) {}
    },

    // Effect to process socket notification
    *processSocketNotification({ payload }, { put }) {
      try {
        const [eventType, eventData] = payload;

        let notificationData = {};

        // Process according to event type
        switch (eventType) {
          case 'entity':
            notificationData = yield put.resolve({
              type: 'processEntityNotification',
              payload: eventData,
            });
            break;

          case 'list':
            notificationData = yield put.resolve({
              type: 'processListNotification',
              payload: eventData,
            });
            break;

          case 'report':
            notificationData = yield put.resolve({
              type: 'processReportNotification',
              payload: eventData,
            });
            break;

          case 'document-export':
            notificationData = yield put.resolve({
              type: 'processDocumentExportNotification',
              payload: eventData,
            });
            break;

          default:
            // For future types
            notificationData = {
              type: eventType,
              title: formatMessage({ id: 'notifications.generic.title' }),
              message: formatMessage({ id: 'notifications.generic.message' }),
              data: eventData,
            };
        }

        if (notificationData && shouldAddSocketNotification(notificationData)) {
          yield put({
            type: 'addNotification',
            payload: notificationData,
          });
        }
      } catch (error) {}
    },

    // Specific effect to process entity notifications
    *processEntityNotification({ payload }, { put }) {
      try {
        const {
          operationType,
          taskStatus,
          entityName,
          taskNumber,
          taskOutput,
          companyName,
          environmentName,
        } = payload;

        let title = '';
        let message = '';
        let priority = CoreEnums.NotificationPriority.Normal;
        let actions = [];

        // Process according to operation type
        // TODO: Refactor this nested if-else logic using a combination of:
        // 1. Dictionary/object mapping for operation type -> handler functions
        // 2. Switch statements within handlers for status-specific logic
        // 3. This will improve readability, maintainability and testing
        if (operationType === CoreEnums.DynamicEntityInternalTaskOperationType.Export) {
          if (taskStatus === CoreEnums.InternalTaskStatus.Processed) {
            // Completed successfully
            title = formatMessage({ id: 'notifications.export.completed.title' });
            message = formatMessage(
              { id: 'notifications.export.completed.message' },
              { entityName },
            );
            priority = CoreEnums.NotificationPriority.High;

            // Add download action if file is available
            if (taskOutput?.fileStoragePublicUrl) {
              actions.push({
                type: 'download',
                label: formatMessage({ id: 'notifications.action.download' }),
                url: taskOutput.fileStoragePublicUrl,
                fileName: taskOutput.fileName,
              });
            }
          } else if (taskStatus === CoreEnums.InternalTaskStatus.Failed) {
            title = formatMessage({ id: 'notifications.export.failed.title' });
            message = formatMessage({ id: 'notifications.export.failed.message' }, { entityName });
            priority = CoreEnums.NotificationPriority.High;

            // Add retry action if possible
            actions.push({
              type: 'retry',
              label: formatMessage({ id: 'notifications.action.retry' }),
              entityId: payload.entityId,
            });
          } else if (taskStatus === CoreEnums.InternalTaskStatus.Processing) {
            title = formatMessage({ id: 'notifications.export.inprogress.title' });
            message = formatMessage(
              { id: 'notifications.export.inprogress.message' },
              { entityName },
            );
            priority = CoreEnums.NotificationPriority.Normal;
          } else if (taskStatus === CoreEnums.InternalTaskStatus.Pending) {
            title = formatMessage({ id: 'notifications.export.queued.title' });
            message = formatMessage({ id: 'notifications.export.queued.message' }, { entityName });
            priority = CoreEnums.NotificationPriority.Normal;
          }
        } else if (operationType === CoreEnums.DynamicEntityInternalTaskOperationType.Import) {
          if (taskStatus === CoreEnums.InternalTaskStatus.Processed) {
            // Completed successfully
            title = formatMessage({ id: 'notifications.import.completed.title' });
            message = formatMessage(
              { id: 'notifications.import.completed.message' },
              { entityName },
            );
            priority = CoreEnums.NotificationPriority.High;
          } else if (taskStatus === CoreEnums.InternalTaskStatus.Failed) {
            title = formatMessage({ id: 'notifications.import.failed.title' });
            message = formatMessage({ id: 'notifications.import.failed.message' }, { entityName });
            priority = CoreEnums.NotificationPriority.High;
          } else if (taskStatus === CoreEnums.InternalTaskStatus.Processing) {
            title = formatMessage({ id: 'notifications.import.inprogress.title' });
            message = formatMessage(
              { id: 'notifications.import.inprogress.message' },
              { entityName },
            );
            priority = CoreEnums.NotificationPriority.Normal;
          }
        }

        // Create notification object
        const notificationData = {
          id: _.uniqueId('notification_entity_'),
          type: 'entity',
          title,
          message,
          priority,
          actions,
          data: {
            ...payload,
            operationTypeText: getOperationTypeText(operationType),
            statusText: getStatusText(taskStatus),
          },
          timestamp: payload.created
            ? moment.utc(payload.created).toISOString()
            : moment.utc().toISOString(),
        };

        return notificationData;
      } catch (error) {
        return null;
      }
    },

    // Specific effect to process list notifications
    *processListNotification({ payload }, { put }) {
      try {
        const {
          operationType,
          taskStatus,
          listName,
          taskNumber,
          taskOutput,
          companyName,
          environmentName,
        } = payload;

        let title = '';
        let message = '';
        let priority = CoreEnums.NotificationPriority.Normal;
        let actions = [];

        // TODO: Refactor this nested if-else logic using a combination of:
        // 1. Dictionary/object mapping for operation type -> handler functions
        // 2. Switch statements within handlers for status-specific logic
        // 3. This will improve readability, maintainability and testing
        // Process according to operation type
        if (operationType === CoreEnums.CustomListInternalTaskOperationType.Export) {
          if (taskStatus === CoreEnums.InternalTaskStatus.Processed) {
            // Completed successfully
            title = formatMessage({ id: 'notifications.list.export.completed.title' });
            message = formatMessage(
              { id: 'notifications.list.export.completed.message' },
              { listName },
            );
            priority = CoreEnums.NotificationPriority.High;

            // Add download action if file is available
            if (taskOutput?.fileStoragePublicUrl) {
              actions.push({
                type: 'download',
                label: formatMessage({ id: 'notifications.action.download' }),
                url: taskOutput.fileStoragePublicUrl,
                fileName: taskOutput.fileName,
              });
            }
          } else if (taskStatus === CoreEnums.InternalTaskStatus.Processing) {
            // In progress
            title = formatMessage({ id: 'notifications.list.export.inprogress.title' });
            message = formatMessage(
              { id: 'notifications.list.export.inprogress.message' },
              { listName },
            );
          } else if (taskStatus === CoreEnums.InternalTaskStatus.Pending) {
            // Queued
            title = formatMessage({ id: 'notifications.list.export.queued.title' });
            message = formatMessage(
              { id: 'notifications.list.export.queued.message' },
              { listName },
            );
          } else if (
            taskStatus === CoreEnums.InternalTaskStatus.Error ||
            taskStatus === CoreEnums.InternalTaskStatus.Failed
          ) {
            // Failed
            title = formatMessage({ id: 'notifications.list.export.failed.title' });
            message = formatMessage(
              { id: 'notifications.list.export.failed.message' },
              { listName },
            );
            priority = CoreEnums.NotificationPriority.High;
          }
        }
        // Add future support for import operations
        else if (operationType === 2) {
          // Import operation (placeholder for future use)
          if (taskStatus === CoreEnums.InternalTaskStatus.Processed) {
            title = formatMessage({ id: 'notifications.list.import.completed.title' });
            message = formatMessage(
              { id: 'notifications.list.import.completed.message' },
              { listName },
            );
            priority = CoreEnums.NotificationPriority.High;
          } else if (taskStatus === CoreEnums.InternalTaskStatus.Processing) {
            title = formatMessage({ id: 'notifications.list.import.inprogress.title' });
            message = formatMessage(
              { id: 'notifications.list.import.inprogress.message' },
              { listName },
            );
          } else if (
            taskStatus === CoreEnums.InternalTaskStatus.Error ||
            taskStatus === CoreEnums.InternalTaskStatus.Failed
          ) {
            title = formatMessage({ id: 'notifications.list.import.failed.title' });
            message = formatMessage(
              { id: 'notifications.list.import.failed.message' },
              { listName },
            );
            priority = CoreEnums.NotificationPriority.High;
          }
        }

        // Return notification data
        const notificationData = {
          id: _.uniqueId('notification_list_'),
          type: CoreEnums.NotificationType.List,
          title,
          message,
          priority,
          actions,
          data: {
            ...payload,
            entityName: listName, // Map listName to entityName for compatibility with UI
            operationText: getOperationTypeText(operationType),
            statusText: getStatusText(taskStatus),
          },
          timestamp: payload.created
            ? moment.utc(payload.created).toISOString()
            : moment.utc().toISOString(),
        };

        return notificationData;
      } catch (error) {
        return null;
      }
    },

    *processDocumentExportNotification({ payload }) {
      try {
        const { taskStatus, taskNumber, taskOutput, observations } = payload;

        let title = '';
        let message = '';
        let priority = CoreEnums.NotificationPriority.Normal;
        let actions = [];

        const status = taskStatus;
        const isProcessed =
          status === CoreEnums.InternalTaskStatus.Processed || status === 'processed';
        const isError =
          status === CoreEnums.InternalTaskStatus.Error ||
          status === CoreEnums.InternalTaskStatus.Failed ||
          status === 'error';
        const isProcessing =
          status === CoreEnums.InternalTaskStatus.Processing || status === 'processing';
        const isPending = status === CoreEnums.InternalTaskStatus.Pending || status === 'pending';

        if (isProcessed) {
          title = formatMessage({ id: 'notifications.documentExport.completed.title' });
          message = formatMessage(
            { id: 'notifications.documentExport.completed.message' },
            { taskNumber: taskNumber || '' },
          );
          priority = CoreEnums.NotificationPriority.High;

          const documentId = taskOutput?.documentId;
          if (documentId) {
            const { generatedPublicFileURL } = generatePublicFileURL('documents', {
              dataId: documentId,
              postfix: 'download',
            });
            actions.push({
              type: 'download',
              label: formatMessage({ id: 'notifications.action.download' }),
              url: generatedPublicFileURL,
              fileName: taskOutput?.fileName,
            });
          }
        } else if (isError) {
          title = formatMessage({ id: 'notifications.documentExport.failed.title' });
          message = observations
            ? formatMessage(
                { id: 'notifications.documentExport.failed.messageDetail' },
                { taskNumber: taskNumber || '', observations },
              )
            : formatMessage(
                { id: 'notifications.documentExport.failed.message' },
                { taskNumber: taskNumber || '' },
              );
          priority = CoreEnums.NotificationPriority.High;
        } else if (isProcessing) {
          title = formatMessage({ id: 'notifications.documentExport.inprogress.title' });
          message = formatMessage(
            { id: 'notifications.documentExport.inprogress.message' },
            { taskNumber: taskNumber || '' },
          );
        } else if (isPending) {
          title = formatMessage({ id: 'notifications.documentExport.queued.title' });
          message = formatMessage(
            { id: 'notifications.documentExport.queued.message' },
            { taskNumber: taskNumber || '' },
          );
        }

        return {
          id: _.uniqueId('notification_document_export_'),
          type: 'document-export',
          title,
          message,
          priority,
          actions,
          data: {
            ...payload,
            operationType: payload?.operationType || 'export',
            operationTypeText: formatMessage({ id: 'notifications.operationType.export' }),
            statusText: getStatusText(taskStatus),
          },
          timestamp: payload.created
            ? moment.utc(payload.created).toISOString()
            : moment.utc().toISOString(),
        };
      } catch (error) {
        return null;
      }
    },

    *processReportNotification({ payload }) {
      try {
        const {
          operationType,
          taskStatus,
          reportName,
          taskNumber,
          taskOutput,
          observations,
        } = payload;

        let title = '';
        let message = '';
        let priority = CoreEnums.NotificationPriority.Normal;
        let actions = [];

        const isExport =
          operationType === CoreEnums.ReportInternalTaskOperationType.Export ||
          operationType === 'export';

        if (isExport) {
          if (taskStatus === CoreEnums.InternalTaskStatus.Processed) {
            title = formatMessage({ id: 'notifications.report.export.completed.title' });
            message = formatMessage(
              { id: 'notifications.report.export.completed.message' },
              { reportName },
            );
            priority = CoreEnums.NotificationPriority.High;

            if (taskOutput?.fileStoragePublicUrl) {
              actions.push({
                type: 'download',
                label: formatMessage({ id: 'notifications.action.download' }),
                url: taskOutput.fileStoragePublicUrl,
                fileName: taskOutput.fileName,
              });
            }
          } else if (
            taskStatus === CoreEnums.InternalTaskStatus.Error ||
            taskStatus === CoreEnums.InternalTaskStatus.Failed
          ) {
            title = formatMessage({ id: 'notifications.report.export.failed.title' });
            message = observations
              ? formatMessage(
                  { id: 'notifications.report.export.failed.messageDetail' },
                  { reportName, observations },
                )
              : formatMessage({ id: 'notifications.report.export.failed.message' }, { reportName });
            priority = CoreEnums.NotificationPriority.High;
          } else if (taskStatus === CoreEnums.InternalTaskStatus.Processing) {
            title = formatMessage({ id: 'notifications.report.export.inprogress.title' });
            message = formatMessage(
              { id: 'notifications.report.export.inprogress.message' },
              { reportName },
            );
          } else if (taskStatus === CoreEnums.InternalTaskStatus.Pending) {
            title = formatMessage({ id: 'notifications.report.export.queued.title' });
            message = formatMessage(
              { id: 'notifications.report.export.queued.message' },
              { reportName },
            );
          } else if (taskStatus === CoreEnums.InternalTaskStatus.Cancelled) {
            title = formatMessage({ id: 'notifications.report.export.cancelled.title' });
            message = formatMessage(
              { id: 'notifications.report.export.cancelled.message' },
              { reportName },
            );
          }
        }

        const notificationData = {
          id: _.uniqueId('notification_report_'),
          type: 'report',
          title,
          message,
          priority,
          actions,
          data: {
            ...payload,
            operationTypeText: formatMessage({ id: 'notifications.operationType.export' }),
            statusText: getStatusText(taskStatus),
          },
          timestamp: payload.created
            ? moment.utc(payload.created).toISOString()
            : moment.utc().toISOString(),
        };

        return notificationData;
      } catch (error) {
        return null;
      }
    },
  },

  reducers: {
    // Update drawer visibility
    updateDrawerVisible(state, { payload }) {
      return {
        ...state,
        drawerVisible: payload,
      };
    },

    // Update notifications and counter
    updateNotifications(state, { payload }) {
      const nextNotifications = payload.notifications ?? state.notifications;
      const nextUnreadCount =
        typeof payload.unreadCount === 'number'
          ? payload.unreadCount
          : calculateUnreadCount(nextNotifications);

      return {
        ...state,
        notifications: nextNotifications,
        unreadCount: nextUnreadCount,
        ...(payload.pagination && {
          pagination: {
            ...state.pagination,
            ...payload.pagination,
          },
        }),
        ...(payload.hasMore !== undefined && { hasMore: payload.hasMore }),
        ...(payload.initialized !== undefined && { initialized: payload.initialized }),
      };
    },

    // Update loading state
    updateLoading(state, { payload }) {
      return {
        ...state,
        loading: payload,
      };
    },

    setFetchingMore(state, { payload }) {
      return {
        ...state,
        isFetchingMore: payload,
      };
    },

    setError(state, { payload }) {
      return {
        ...state,
        error: payload,
      };
    },

    setUnreadCount(state, { payload }) {
      const normalized = Number(payload);
      return {
        ...state,
        unreadCount:
          Number.isFinite(normalized) && normalized >= 0 ? normalized : state.unreadCount,
      };
    },

    // Update filters
    updateFilters(state, { payload }) {
      return {
        ...state,
        filters: {
          ...state.filters,
          ...payload,
        },
      };
    },

    // Reset state
    resetState(state) {
      return initialState;
    },
  },
};

export default NotificationsModel;
